import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { canManagePracticeCourses } from '../models/user-role.types';

export const practiceCoursesGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const featureFlagService = inject(FeatureFlagService);
  const router = inject(Router);
  const decide = () =>
    canManagePracticeCourses(authService.currentUser(), Boolean(featureFlagService.getFlagVariant('PracticeCoursesFeature'))) ||
    router.createUrlTree(['/main/dashboard']);

  // After boot the user and the flags are both in. `toObservable` leaves an effect behind until the route's
  // injector is destroyed, so it is only for the navigations that run before then.
  if (authService.currentUser() !== null && featureFlagService.flagsLoaded()) return decide();

  // Waits for the flag payload as well as the user. A guard decides once, so reading the flag before
  // PostHog has answered would redirect a legitimate manager away from a deep link or a hard refresh.
  // `flagsLoaded` resolves either when the payload arrives or when the service stops waiting for it.
  return combineLatest([toObservable(authService.currentUser), toObservable(featureFlagService.flagsLoaded)]).pipe(
    filter(([user, flagsLoaded]) => user !== null && flagsLoaded),
    take(1),
    map(decide)
  );
};
